import { consume } from "@lit/context";
import {
    fileIcon,
    formatDate,
    getEmployeeName,
    JobApplicationDTO,
    JobApplicationStatus,
    type JobPostingDTO,
    JobPostingStatus,
    type MimeType,
    SaveJobApplicationNoteDTO,
} from "@pentacode/core";
import { css, html, nothing } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import "../../../components/avatar/avatar";
import type { Avatar } from "../../../components/avatar/avatar";
import { Textarea } from "../../../components/text-area/textarea";
import { enabledFeatures, type EnabledFeatures } from "../../../contexts/features-context";
import { alert, confirm } from "../../../elements/alert-dialog";
import { Dialog } from "../../../elements/dialog";
import "../../../elements/popover";
import type { Popover } from "../../../elements/popover";
import "../../../elements/scroller";
import { app, router } from "../../../init";

type JobApplicationAction =
    | { action: "approve" }
    | { action: "reject"; sendEmail: boolean }
    | { action: "delete" }
    | { action: null };

export type JobApplicationResult = JobApplicationAction & { notes: string };

export type JobApplicationInput = {
    application: JobApplicationDTO;
    posting: JobPostingDTO;
    showPostingLink?: boolean;
};

@customElement("ptc-job-application-dialog")
export class JobApplicationDialog extends Dialog<JobApplicationInput, JobApplicationResult> {
    readonly preventDismiss = true;

    @state()
    private _application: JobApplicationDTO;

    @state()
    private _posting: JobPostingDTO;

    @state()
    private _showPostingLink = false;

    @consume({ context: enabledFeatures, subscribe: true })
    private _enabledFeatures: EnabledFeatures;

    show({
        application,
        posting,
        showPostingLink = false,
    }: {
        application: JobApplicationDTO;
        posting: JobPostingDTO;
        showPostingLink?: boolean;
    }) {
        this._application = new JobApplicationDTO(application);
        this._posting = posting;
        this._showPostingLink = showPostingLink;
        return super.show();
    }

    protected done(result: JobApplicationAction = { action: null }) {
        super.done({ ...result, notes: this._application.notes ?? "" });
    }

    private _openPosting() {
        this.done({ action: null });
        router.go(`recruiting/${this._posting.id}`);
    }

    private _approve() {
        this.done({ action: "approve" });
    }

    private _reject(sendEmail: boolean) {
        this.done({ action: "reject", sendEmail });
    }

    private async _delete() {
        if (
            !(await confirm(
                `Möchten Sie die Bewerbung von ${this._application.firstName} ${this._application.lastName} wirklich endgültig löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
                "Löschen",
                "Abbrechen",
                { type: "destructive", icon: "icon.action.delete", title: "Bewerbung Löschen" }
            ))
        ) {
            return;
        }

        this.done({ action: "delete" });
    }

    private get _isDeletable(): boolean {
        return (
            app.hasPermission("manage.recruiting") &&
            this._posting?.status === JobPostingStatus.Archived &&
            this._application.status !== JobApplicationStatus.Accepted
        );
    }

    private _sendMessage() {
        window.open(`mailto:${this._application.email}?subject=${encodeURIComponent(this._posting.title)}`);
    }

    @query("ptc-popover.note-popover")
    private _notePopover: Popover;

    @query("ptc-textarea[name='notes']")
    private _noteInput: Textarea;

    private async _saveNote() {
        const notes = this._noteInput.value;
        try {
            await app.api.saveJobApplicationNote(
                new SaveJobApplicationNoteDTO({ applicationId: this._application.id, notes })
            );
        } catch (e) {
            void alert(e.message, { type: "warning" });
            return;
        }
        this._application.notes = notes;
        this.requestUpdate();
        await this.updateComplete;
        this._notePopover.hide();
    }

    private async _discardNote() {
        this._noteInput.value = this._application.notes ?? "";
        this.requestUpdate();
        await this.updateComplete;
        this._notePopover.hide();
    }

    static styles = [
        ...Dialog.styles,
        Textarea.styles,
        css`
            :host {
                --dialog-max-width: 600px;
            }

            .details-row {
                display: flex;
                gap: 1em;
            }

            .details-row > .field,
            .identity {
                flex: 1 1 0;
                min-width: 0;
            }

            label {
                padding: 0;
                margin: 0.5em;
                color: var(--color-primary);
            }

            ptc-avatar {
                margin: 0.3em;
            }

            .field-value {
                min-width: 0;
                overflow-wrap: anywhere;
                word-break: break-word;
            }

            pre.field-value {
                white-space: pre-wrap;
            }

            .documents-box {
                margin-top: 1em;
                padding: 1em;
                background: var(--color-bg-secondary, #f7f7f7);
                border-radius: 8px;
                box-sizing: border-box;
            }
            .documents-box a.box {
                padding: 0.5em 1em;
                margin-bottom: 0.5em;
                border-radius: 6px;
                background: var(--color-bg-tertiary, #fff);
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
            }

            .header {
                display: grid;
                grid-template-columns: auto repeat(3, min-content);
                grid-template-rows: auto;
                align-items: center;
                gap: var(--spacing);
            }

            .note-popover {
                width: 26em;
            }

            .note-popover ptc-textarea textarea {
                min-height: 7em;
            }

            .note-button.has-note ptc-ui-icon {
                color: var(--orange);
            }

            .note-display {
                cursor: pointer;
                background: var(--orange-bg);
                border-left: 3px solid var(--orange);
            }

            .note-display > ptc-ui-icon:first-child {
                color: var(--orange);
                font-size: 1.2em;
            }

            .note-display .note-text {
                margin: 0.2em 0 0;
                white-space: pre-wrap;
            }
        `,
    ];

    renderActionButtons() {
        if (this._application.status !== JobApplicationStatus.Open) {
            return html`<div class="padded">
                <div class="horizontal spacing evenly stretching layout">
                    <button class="ghost" @click=${this._sendMessage}>
                        <ptc-ui-icon name="icon.action.send-message"></ptc-ui-icon> Nachricht Senden
                    </button>
                </div>
            </div>`;
        }

        return html`<div class="padded">
            <div class="horizontal spacing evenly stretching layout">
                <button class="ghost" @click=${this._sendMessage}>
                    <ptc-ui-icon name="icon.action.send-message"></ptc-ui-icon> Nachricht Senden
                </button>
            </div>

            <div class="horizontal top-margined spacing evenly stretching layout">
                <button class="primary" @click=${this._approve}>
                    <ptc-ui-icon name="icon.action.hire"></ptc-ui-icon> Einstellen
                </button>
            </div>

            ${this._enabledFeatures?.recruitingUpdate
                ? html` <div class="horizontal top-margined spacing evenly stretching layout">
                      <button class="negative" @click=${() => this._reject(true)}>
                          <ptc-ui-icon name="icon.action.send-message"></ptc-ui-icon>
                          <ptc-ui-icon name="icon.action.reject-application"></ptc-ui-icon> Mit Email Ablehnen
                      </button>
                      <button class="negative" @click=${() => this._reject(false)}>
                          <ptc-ui-icon name="icon.action.reject-application"></ptc-ui-icon> Ohne Email Ablehnen
                      </button>
                  </div>`
                : html`<div class="horizontal top-margined spacing evenly stretching layout">
                      <button class="negative" @click=${() => this._reject(false)}>
                          <ptc-ui-icon name="icon.action.reject-application"></ptc-ui-icon> Ablehnen
                      </button>
                  </div>`}
        </div>`;
    }

    renderContent() {
        const {
            email,
            firstName,
            lastName,
            headShotUrl,
            birthday,
            availableFrom,
            address,
            postalCode,
            city,
            phone,
            created,
            message,
            documents,
        } = this._application;
        const avatarEmployee: Avatar["employee"] = {
            firstName,
            lastName,
            name: getEmployeeName({
                firstName: this._application.firstName,
                lastName: this._application.lastName,
                callName: "",
            }),
            avatar: headShotUrl,
        };

        const hasNote = !!this._application.notes?.trim();

        return html`
            <div class="vertical layout fit-vertically">
                <div class="header double-padded">
                    <div>Bewerbung vom ${formatDate(created)}</div>
                    <button class="slim transparent note-button ${hasNote ? "has-note" : ""}">
                        ${hasNote
                            ? html`<ptc-ui-icon name="icon.object.note:active"></ptc-ui-icon>`
                            : html`<ptc-ui-icon name="icon.action.edit"></ptc-ui-icon>`}
                    </button>

                    <ptc-popover
                        @popover-show=${() => this._noteInput.updateSize()}
                        @popover-hide=${this._discardNote}
                        class="note-popover unpadded"
                        trigger="click"
                        alignment="bottom-left"
                    >
                        <div class="padded center-aligning horizontal spacing layout border-bottom">
                            <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon>
                            <div class="semibold stretch">Notiz</div>
                        </div>
                        <div class="padded vertical layout">
                            <ptc-textarea
                                name="notes"
                                placeholder="Notiz zur Bewerbung..."
                                .maxLength=${1000}
                                .value=${this._application.notes ?? ""}
                            ></ptc-textarea>
                            <div class="horizontal spacing evenly stretching top-margined layout">
                                <button class="primary" @click=${this._saveNote}>Speichern</button>
                                <button class="transparent" @click=${this._discardNote}>Abbrechen</button>
                            </div>
                        </div>
                    </ptc-popover>

                    ${this._isDeletable
                        ? html` <button class="slim transparent" @click=${this._delete}>
                              <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                          </button>`
                        : nothing}
                    ${this._showPostingLink
                        ? html`
                              <button class="ghost slim no-wrap" @click=${this._openPosting}>
                                  Stelle ansehen <ptc-ui-icon name="icon.navigation.external-link"></ptc-ui-icon>
                              </button>
                          `
                        : nothing}
                    <button class="slim transparent" @click=${() => this.done()}>
                        <ptc-ui-icon name="icon.action.close"></ptc-ui-icon>
                    </button>
                </div>
                <ptc-scroller class="stretch">
                    <div class="double-padded">
                        <div class="horizontal center-aligning spacing layout">
                            <ptc-avatar class="enormous" .employee=${avatarEmployee}></ptc-avatar>
                            <div class="stretch identity">
                                <div class="field">
                                    <label>Vorname</label>
                                    <div class="margined field-value">${firstName}</div>
                                </div>
                                <div class="field">
                                    <label>Nachname</label>
                                    <div class="margined field-value">${lastName}</div>
                                </div>
                            </div>
                        </div>
                        <div class="details-row horizontal spacing evenly stretching layout">
                            <div class="field">
                                <label> Geburtstag </label>
                                <div class="margined field-value">
                                    ${birthday ? formatDate(birthday) : "Keine Angabe"}
                                </div>
                            </div>
                            <div class="field">
                                <label> Verfügbar Ab </label>
                                <div class="margined field-value">
                                    ${availableFrom ? formatDate(availableFrom) : "sofort"}
                                </div>
                            </div>
                        </div>
                        <div class="details-row horizontal spacing evenly stretching layout">
                            <div class="field">
                                <label> Email </label>
                                <div class="margined field-value">${email}</div>
                            </div>
                            <div class="field">
                                <label> Telefon </label>
                                <div class="margined field-value">
                                    ${phone || html`<span class="subtle">Keine Angabe</span>`}
                                </div>
                            </div>
                        </div>
                        <div class="field">
                            <label> Adresse </label>
                            <div class="margined field-value">${address}</div>
                        </div>
                        <div class="details-row horizontal spacing evenly stretching layout">
                            <div class="field">
                                <label> Postleitzahl </label>
                                <div class="margined field-value">${postalCode}</div>
                            </div>
                            <div class="field">
                                <label> Stadt </label>
                                <div class="margined field-value">${city}</div>
                            </div>
                        </div>
                        <div class="field">
                            <label> Nachricht </label>
                            <div class="margined">
                                ${message
                                    ? html`<pre class="field-value">${message}</pre>`
                                    : html`<span class="subtle"> Keine Nachricht hinterlassen.</span>`}
                            </div>
                        </div>
                        ${this._enabledFeatures?.recruitingUpdate
                            ? html`<div class="field application-field">
                                  <label>Dokumente</label>
                                  <div class="documents-box vertical layout">
                                      ${documents?.length
                                          ? documents.map(
                                                (doc) => html`
                                                    <a
                                                        class="horizontal center-aligning layout"
                                                        href=${doc.downloadUrl}
                                                        target="_blank"
                                                        rel="noopener"
                                                    >
                                                        <ptc-ui-icon
                                                            name="${fileIcon(doc.type as MimeType)}"
                                                            class="larger"
                                                        ></ptc-ui-icon>
                                                        <div class="stretch ellipsis">${doc.name}</div>
                                                        <ptc-ui-icon name="icon.navigation.external-link"></ptc-ui-icon>
                                                    </a>
                                                `
                                            )
                                          : html`<div class="subtle">Keine Dokumente hinterlassen.</div>`}
                                  </div>
                              </div>`
                            : nothing}
                    </div>
                </ptc-scroller>
                ${this.renderActionButtons()}
            </div>
        `;
    }
}
